import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { cleanAddress } from "./UtilsGoogleMap";
import { useTranslation } from "react-i18next";
import intlConstants from "../../../config/intlConstants";

const libraries = ["places"];

const AddressAutocomplete = ({
  onPlaceSelect,
  formValues,
  fieldConfig,
  setFirstLoad,
  apiKey,
  language,
  restrictedCountry,
  handleOnBlur
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
    language
  });

  const { t } = useTranslation();
  const searchBoxRef = useRef(null);
  const [inputValue, setInputValue] = useState(formValues[fieldConfig.id] ?? "");
  const [inputSelectedValue, setInputSelectedValue] = useState(formValues[fieldConfig.id] ?? "");
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  const onLoad = useCallback(ref => {
    searchBoxRef.current = ref;
  }, []);

  const getRestrictedCountry = useCallback(
    country => {
      const municipalSelected = formValues[fieldConfig.references.municipal]?.label ?? "";
      return country.toLowerCase() == intlConstants.CHINA_CODE &&
        municipalSelected.toLowerCase() == intlConstants.HONG_KONG_NAME
        ? intlConstants.HONG_KONG_CODE
        : country;
    },
    [fieldConfig.references.municipal, formValues]
  );

  const options = useMemo(
    () => ({
      componentRestrictions: {
        country: getRestrictedCountry(restrictedCountry) || undefined
      }
    }),
    [restrictedCountry, getRestrictedCountry]
  );

  useEffect(() => {
    if (formValues[fieldConfig.name]) {
      setInputSelectedValue(formValues[fieldConfig.name]);
      setInputValue(formValues[fieldConfig.name]);
    }
  }, [formValues, fieldConfig]);

  const getFullSearchValue = useCallback(
    value => {
      if (formValues[fieldConfig.references.country]) {
        const countryRef =
          formValues[fieldConfig.references.country].label ?? formValues[fieldConfig.references.country];
        const regionRef = fieldConfig.references.region
          ? (formValues[fieldConfig.references.region].label ?? formValues[fieldConfig.references.region])
          : null;
        const cityRef = fieldConfig.references.city
          ? (formValues[fieldConfig.references.city].label ?? formValues[fieldConfig.references.city])
          : null;
        const municipalRef = fieldConfig.references.municipal
          ? (formValues[fieldConfig.references.municipal].label ?? formValues[fieldConfig.references.municipal])
          : null;

        const splitAddress = [
          value,
          cleanAddress(municipalRef),
          cleanAddress(cityRef),
          cleanAddress(regionRef),
          cleanAddress(countryRef)
        ].filter(Boolean);
        return splitAddress.join(", ");
      }
      return value;
    },
    [fieldConfig.references, formValues]
  );

  const onInputChange = e => {
    const userValue = e.target.value;
    setInputValue(userValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const fullQuery = getFullSearchValue(userValue);
      if (inputRef.current && fullQuery) {
        const inputEl = inputRef.current;
        const original = inputEl.value;
        inputEl.value = fullQuery;
        window.google.maps.event.trigger(inputEl, "input");
        setTimeout(() => {
          inputEl.value = original;
        }, 0);
      }
    }, 300);
  };

  const onBlur = () => {
    setFirstLoad(false);
    handleOnBlur(inputValue, inputSelectedValue);
  };

  const handlePlaceChanged = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    const place = searchBoxRef.current.getPlace();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!place?.geometry) {
      return;
    }

    if (restrictedCountry && place.address_components) {
      const countryComponent = place.address_components.find(c => c.types.includes("country"));
      const restrictedCountryValue = getRestrictedCountry(restrictedCountry);

      if (countryComponent) {
        const cc = countryComponent.short_name.toLowerCase();
        const cn = countryComponent.long_name.toLowerCase();
        const rc = restrictedCountryValue.toLowerCase();
        if (cc !== rc && cn !== rc) {
          return;
        }
      }
    }

    onPlaceSelect(place);
    setInputValue(place.formatted_address ?? "");
    setInputSelectedValue(place.formatted_address ?? "");
  }, [onPlaceSelect, restrictedCountry, getRestrictedCountry]);

  if (loadError) {
    return (
      <div style={{ padding: "10px 0" }}>
        <input
          type="text"
          className="google-srch-box"
          value={t("content.errorGoogleAddress")}
          style={{ color: "#d60303" }}
          disabled={true}
        />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: "10px 0" }}>
        <input
          type="text"
          placeholder={t("content.loadingGoogleAddress")}
          className="google-srch-box"
          disabled={true}
        />
      </div>
    );
  }

  const checkRequiredFields = () => {
    if (!fieldConfig.requiredFields || fieldConfig.requiredFields.length < 1) {
      return true;
    }

    const found = fieldConfig.requiredFields.find(fieldName => {
      return !formValues[fieldName];
    });
    return found ? false : true;
  };

  return (
    <div style={{ padding: "10px 0" }}>
      <Autocomplete onLoad={onLoad} onPlaceChanged={handlePlaceChanged} options={options}>
        <input
          ref={inputRef}
          type="text"
          placeholder={fieldConfig.placeHolder}
          className="google-srch-box"
          value={inputValue}
          onChange={onInputChange}
          onBlur={onBlur}
          disabled={!checkRequiredFields()}
        />
      </Autocomplete>
    </div>
  );
};

export default AddressAutocomplete;
